import React, { useState } from "react";
import HeaderItem from "./HeaderItem";
import { sidebarMenu } from "./MenuHelper";
import MenuItem from "./MenuItem";
import { FOCUS_RING } from "Components/DesignSystem/focusRing";

// Helper function to convert menu name to kebab-case ID
const menuNameToId = (menuName: string): string => {
  return menuName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

/**
 * The app's primary navigation. Mounted by `Layout` on every authenticated page.
 *
 * Restyled onto the design system 2026-08-30 — `okf/templates/e2e.md` § 18. Two
 * structural notes, both load-bearing:
 *
 * **It is a `<nav>`.** It was a bare `<div>` while the top bar (`Searchbar`) already
 * had `<nav data-testid="main-navigation">`, so the app's *primary* navigation was
 * the one that wasn't a landmark.
 *
 * **Each group is its own `<ul>`,** labelled by its `HeaderItem`. The list used to be
 * flat, with group headers as sibling `<li>`s among the items, which a screen reader
 * reads as one 26-item list with eight stray entries in it. The menu-item `<li>`s and
 * every `id`/`data-testid` on them are untouched by the nesting.
 *
 * **`tw-transition-[width]`, not `tw-transition-width`.** The latter is not a class
 * Tailwind generates — verified absent from the built stylesheet — so the collapse
 * has never animated, it snapped. Exactly the `tw-no-scrollbar` failure of § 168, on
 * this same element: an unknown class is not an error in JSX, in `tsc`, or in the JIT
 * scan. `Sidebar.classGuard.test.ts` now checks both families against Tailwind's own
 * resolved config so the next one fails a test instead of shipping.
 */
const Sidebar = () => {
  const [showMenu, setShowMenu] = useState<boolean>(true);
  const handleClick = (): void => {
    setShowMenu((prevShowMenu) => !prevShowMenu);
  };

  return (
    <nav
      id="sidebar-container"
      data-testid="sidebar"
      aria-label="Main navigation"
      className={`tw-bg-surface tw-min-h-full tw-border-r tw-border-edge tw-max-h-full tw-overflow-y-auto no-scrollbar tw-pt-3 ${
        showMenu ? "tw-w-60" : "tw-w-16"
      } tw-transition-[width] tw-duration-100 `}
    >
      {/*
        Position deliberately unchanged. `#sidebar-container` is not `relative`, so
        this resolves against `Layout`'s content row — which is what keeps the chip
        pinned while the sidebar scrolls, and what makes it straddle the sidebar's
        right edge by 8px (measured). Both are the existing design. The only thing
        added here is the focus ring it never had.
      */}
      {/*
        The chip was an <img> of open/close-menu-icon.svg — files that carried their
        OWN chrome: a `fill="white"` box, an `#E5E5EA` border and `#030362` chevrons,
        none of which an <img> lets CSS re-ink, so the chip stayed a white square on
        the dark theme. The box is the button's own tokens now, and the glyph is
        inline SVG on currentColor. One glyph, two directions: the « of the expanded
        state is point-symmetric, so the collapsed » is the same paths rotated 180°.
      */}
      <button
        id="sidebar-toggle-btn"
        data-testid="sidebar-toggle-btn"
        aria-label="Toggle sidebar"
        aria-expanded={showMenu}
        className={`tw-block tw-absolute ${
          showMenu ? "tw-left-56" : "tw-left-12"
        } tw-top-0 tw-transition-[left] tw-duration-100 tw-bg-surface tw-border tw-border-edge tw-text-ink hover:tw-bg-surface-hover tw-z-10 tw-p-0 tw-m-0 tw-h-min tw-rounded-ctl ${FOCUS_RING}`}
        onClick={handleClick}
      >
        <svg
          id="sidebar-toggle-icon"
          aria-hidden="true"
          className={`tw-w-6 tw-aspect-square ${showMenu ? "" : "tw-rotate-180"}`}
          viewBox="0 0 26 26"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M18 8.04761L13.0476 13L18 17.9524" stroke="currentColor" />
          <path d="M13.5237 8.04761L8.5713 13L13.5237 17.9524" stroke="currentColor" />
        </svg>
      </button>
      <ul id="sidebar-menu-list">
        {sidebarMenu.map((header, index) => {
          const headerId = `sidebar-header-${index}`;
          const hasHeader = header.headerName !== "";
          return (
            <li key={index}>
              {hasHeader ? (
                <HeaderItem id={headerId} label={header.headerName} show={showMenu} />
              ) : null}
              <ul
                id={`${headerId}-items`}
                /* Labelled by the visible heading when there is one to point at; by
                   its text directly in the collapsed rail, where the heading is a
                   spacer and `aria-labelledby` would resolve to nothing. */
                aria-labelledby={hasHeader && showMenu ? `${headerId}-label` : undefined}
                aria-label={hasHeader && !showMenu ? header.headerName : undefined}
              >
                {header.menuItems.map((menu, key) => {
                  const menuId = `sidebar-${menuNameToId(menu.menuName)}`;
                  return (
                    <MenuItem
                      id={menuId}
                      key={key}
                      path={menu.path}
                      iconSrc={menu.icon}
                      filledIconSrc={menu.filledIcon}
                      label={menu.menuName}
                      show={showMenu}
                      menu={menu}
                      matchPath={menu.matchPath}
                    />
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default Sidebar;
